import { FC } from 'react';
import { Text, Button, Tooltip } from '@livechat/design-system-react-components';

import { Logo } from 'components/logo/Logo';
import {
  detectProduct,
  FREE_PRODUCTS,
  getProductName,
  getProductDisplayName,
  getAppLinkByName,
} from 'helpers/product';
import { canActivateProduct, getScopes } from 'helpers/account';
import { useProducts } from 'hooks/use-products';
import { useGetMe } from 'hooks/use-get-me';
import { OrganizationGraphQLResponse } from 'hooks/use-organizations';
import { saveItem } from 'helpers/storage/local-storage';
import Amplitude from 'services/Amplitude';
import { pushExternalRedirectToDataLayer } from 'helpers/tracking';
import Location from 'services/Location';
import { parseJwt } from 'helpers/token';

import { GoToProduct } from '../go-to-product/GoToProduct';

import * as styles from './styles';

type Props = {
  organization: OrganizationGraphQLResponse;
  isAccountWhitelisted?: boolean;
  onJoinBlocked(organizationId: string): void;
};

export const DefaultChooserContent: FC<Props> = ({
  organization,
  isAccountWhitelisted,
  onJoinBlocked,
}) => {
  const { email, roles } = useGetMe();

  const currentProductCookie = detectProduct();
  const productToActivate = getProductName(currentProductCookie);
  const productDisplayName = getProductDisplayName(productToActivate);
  const { getRedirectUrl, isFetchingProducts } = useProducts();

  const alreadyHasLC = roles.some((role) => role.product === 'LiveChat');

  const hasProductInOrganization = () =>
    organization.products?.some((product) => product.name === productToActivate);

  const hasLCInThisOrganization = roles.some(
    (role) => role.organization_id === organization.organization_id && role.product === 'LiveChat'
  );

  const isMeOwner = roles.some(
    (role) =>
      role.organization_id === organization.organization_id &&
      role.product === 'Accounts' &&
      role.role === 'owner'
  );

  const generateUnableToStartTrialText = () => {
    if (isAccountWhitelisted && productToActivate) {
      return `This organization doesn’t use ${productDisplayName}. Only organization members can start new trials.`;
    }

    if (!getScopes().includes('organization.product--my:rw')) {
      return `Users with Viewer role can't start trial.`;
    }

    return `Only the Owner can start a LiveChat trial.`;
  };

  const hideLCSignUp = productToActivate === 'LiveChat' && alreadyHasLC;
  const hidePPSignUp = productToActivate === 'PartnerProgram' && !isMeOwner;
  const hideLCInGoToProduct = !hasLCInThisOrganization && alreadyHasLC;
  const state = Location.getHashParam('state') || Location.getUrlParam('state');
  const utmParams = Location.getUtmHashParams();
  const jwtParams = parseJwt(state);

  const handleStartTrialClick = () => {
    saveItem('signup', '1');
    pushExternalRedirectToDataLayer(getAppLinkByName(productToActivate));
    Amplitude.logEvent(email, 'LOGIN_START_TRIAL_CLICK', {}, true);
  };

  const hideGoToProduct =
    !hasLCInThisOrganization &&
    !isAccountWhitelisted &&
    alreadyHasLC &&
    organization.products.length === 1 &&
    organization.products[0].name === 'LiveChat';

  if (isFetchingProducts) {
    return null;
  }

  return (
    <div>
      {hasProductInOrganization() ||
      productToActivate === 'Undefined' ||
      hideLCSignUp ||
      hidePPSignUp ? (
        <div>
          {organization.products?.length > 0 && !hideGoToProduct ? (
            <div>
              <GoToProduct
                titleText="Go to your product"
                organization={organization}
                hideLC={hideLCInGoToProduct}
                isAccountWhitelisted={isAccountWhitelisted}
                onJoinBlocked={onJoinBlocked}
                isMeOwner={isMeOwner}
              />
            </div>
          ) : (
            <Text bold css={styles.noProductsText}>
              You don&apos;t have products in this organization
            </Text>
          )}
        </div>
      ) : (
        <div>
          {((productToActivate === 'PartnerProgram' && isMeOwner) ||
            productToActivate !== 'PartnerProgram') && (
            <div css={styles.topSignUpContainer}>
              <div css={styles.descriptionTextContainer}>
                <div css={styles.signUpForText}>
                  <Text size="md" bold>
                    Sign up for &nbsp;
                  </Text>
                  <Logo product={currentProductCookie} size="medium" />
                  <Text size="md" bold>
                    &nbsp;{productToActivate}
                  </Text>
                </div>
              </div>
              {!canActivateProduct({
                productToActivate,
                organization,
                accountRoles: roles,
              }) ? (
                <Tooltip
                  triggerRenderer={() => (
                    <Button kind="destructive" css={styles.startTrialButton(true)} disabled>
                      {FREE_PRODUCTS.includes(productToActivate)
                        ? `Start for free`
                        : `Start free trial`}
                    </Button>
                  )}
                  kind="invert"
                  placement="top"
                >
                  <Text>{generateUnableToStartTrialText()}</Text>
                </Tooltip>
              ) : (
                <Button
                  kind="primary"
                  css={styles.startTrialButton(false)}
                  href={getRedirectUrl(productToActivate, organization.organization_id, {
                    jwtParams,
                    utmParams,
                  })}
                  onClick={handleStartTrialClick}
                >
                  {FREE_PRODUCTS.includes(productToActivate)
                    ? `Start for free`
                    : `Start free trial`}
                </Button>
              )}
            </div>
          )}
          {organization.products.length > 0 && !hideGoToProduct && (
            <div css={styles.goToTheProductContainer}>
              <GoToProduct
                titleText="Go to the product you already have"
                organization={organization}
                hideLC={alreadyHasLC}
                isAccountWhitelisted={isAccountWhitelisted}
                onJoinBlocked={onJoinBlocked}
                isMeOwner={isMeOwner}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
